import { useMemo } from 'react';
import { Checkbox, Typography, Spin, Empty, Space, Segmented, Divider } from 'antd';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useStationObservations } from '../useStationObservations';
import type { WidgetSettingsProps } from './types';

const { Text } = Typography;

export const StationObservationsSettings = ({
  config,
  onChange,
}: WidgetSettingsProps) => {
  const { selectedEvent } = useEventSelection();
  const { stations, loading } = useStationObservations();

  const columns = (config.columns as number) ?? 2;

  const selectedStations = (config.selectedStations as string[] | undefined) ?? [];
  const allSelected = selectedStations.length === 0;

  const availableStations = useMemo(
    () => stations.map((s) => s.station_reference),
    [stations],
  );

  const handleColumnsChange = (cols: number) => {
    onChange({ columns: cols });
  };

  const handleToggleStation = (station: string, checked: boolean) => {
    let next: string[];
    if (allSelected) {
      next = checked
        ? [station]
        : availableStations.filter((s) => s !== station);
    } else {
      next = checked
        ? [...selectedStations, station]
        : selectedStations.filter((s) => s !== station);
    }

    if (next.length === 0 || next.length === availableStations.length) {
      onChange({ selectedStations: [] });
    } else {
      onChange({ selectedStations: next });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    onChange({ selectedStations: checked ? [] : [availableStations[0]] });
  };

  const renderStationSelection = () => {
    if (!selectedEvent) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select an event to configure stations"
        />
      );
    }

    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Loading stations...</Text>
          </div>
        </div>
      );
    }

    if (availableStations.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No stations available for this event"
        />
      );
    }

    return (
      <>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          Choose which stations to display. When all are selected, the widget shows station selector buttons.
        </Text>

        <Space direction="vertical" size={4}>
          <Checkbox
            checked={allSelected}
            indeterminate={
              !allSelected && selectedStations.length > 0 && selectedStations.length < availableStations.length
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            <Text style={{ fontSize: 13 }}>All stations</Text>
          </Checkbox>

          {availableStations.map((station) => (
            <Checkbox
              key={station}
              checked={allSelected || selectedStations.includes(station)}
              onChange={(e) => handleToggleStation(station, e.target.checked)}
              style={{ marginLeft: 20 }}
            >
              <Text style={{ fontSize: 13 }}>Station {station}</Text>
            </Checkbox>
          ))}
        </Space>
      </>
    );
  };

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Layout
      </Text>
      <Segmented
        block
        value={columns}
        options={[
          { label: 'Single Column', value: 1 },
          { label: 'Double Column', value: 2 },
        ]}
        onChange={(val) => handleColumnsChange(val as number)}
      />

      <Divider style={{ margin: '16px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Visible Stations
      </Text>

      {renderStationSelection()}
    </div>
  );
};
